import React, { useState, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { TierBadge } from '@/components/dashboard/TierBadge';
import { cn, formatLatency, generateSessionId } from '@/lib/utils';
import { useWebSocket } from '@/hooks/useWebSocket';
import api from '@/lib/api';
import { 
  Send, 
  Brain, 
  Trash2, 
  Loader2,
  User,
  Bot,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Clock,
  Coins,
  Wifi,
  WifiOff,
  ThumbsDown,
  Flag
} from 'lucide-react';

// Intent options for feedback
const INTENT_OPTIONS = [
  { value: 'greeting', label: 'Greeting', tier: 'tier1_local_fast' },
  { value: 'farewell', label: 'Farewell', tier: 'tier1_local_fast' },
  { value: 'simple_question', label: 'Simple Question', tier: 'tier1_local_fast' },
  { value: 'factual_lookup', label: 'Factual Lookup', tier: 'tier1_local_fast' },
  { value: 'summarization', label: 'Summarization', tier: 'tier2_local_smart' },
  { value: 'explanation', label: 'Explanation', tier: 'tier2_local_smart' },
  { value: 'comparison', label: 'Comparison', tier: 'tier2_local_smart' },
  { value: 'analysis', label: 'Analysis', tier: 'tier2_local_smart' },
  { value: 'code_generation', label: 'Code Generation', tier: 'tier3_cloud' },
  { value: 'code_debugging', label: 'Code Debugging', tier: 'tier3_cloud' },
  { value: 'complex_reasoning', label: 'Complex Reasoning', tier: 'tier3_cloud' },
  { value: 'creative_writing', label: 'Creative Writing', tier: 'tier3_cloud' },
  { value: 'multi_step_task', label: 'Multi-step Task', tier: 'tier3_cloud' },
];

export function ChatInterface() {
  const [sessionId] = useState(() => generateSessionId());
  const [input, setInput] = useState('');
  const [enableThinking, setEnableThinking] = useState(false);
  const [forceTier, setForceTier] = useState('auto');
  const [expandedThinking, setExpandedThinking] = useState({});
  const [feedbackDialog, setFeedbackDialog] = useState({ open: false, message: null });
  const [selectedCorrection, setSelectedCorrection] = useState('');
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  // Use WebSocket hook for real-time communication
  const { 
    connected, 
    messages, 
    loading, 
    sendMessage, 
    clearMessages,
    reconnect 
  } = useWebSocket(sessionId);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (scrollRef.current) {
      const scrollElement = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollElement) {
        scrollElement.scrollTop = scrollElement.scrollHeight;
      }
    }
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    
    const sent = sendMessage(input.trim(), enableThinking, forceTier);
    if (sent) {
      setInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleThinking = (messageId) => {
    setExpandedThinking(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  const openFeedbackDialog = (message, originalQuery) => {
    setFeedbackDialog({ open: true, message, originalQuery });
    setSelectedCorrection('');
  };

  const handleSubmitFeedback = async () => {
    if (!selectedCorrection || !feedbackDialog.message) return;
    
    const intentOption = INTENT_OPTIONS.find(i => i.value === selectedCorrection);
    
    try {
      await api.submitFeedback({
        query: feedbackDialog.originalQuery,
        predicted_intent: 'unknown', // We'd need to track this from the response
        predicted_tier: feedbackDialog.message.tier,
        predicted_confidence: 0.5,
        corrected_intent: selectedCorrection,
        corrected_tier: intentOption?.tier || 'tier2_local_smart',
        session_id: sessionId
      });
      
      setFeedbackDialog({ open: false, message: null });
      setSelectedCorrection('');
    } catch (err) {
      console.error('Feedback submission failed:', err);
    }
  };

  return (
    <>
    <Card className="border-border bg-card h-[600px] flex flex-col" data-testid="chat-interface">
      <CardHeader className="border-b border-border pb-3 flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="h-5 w-5 text-primary" />
            <CardTitle className="text-sm font-medium">Chat with Ellie</CardTitle>
            
            {/* Connection Status */}
            <Badge 
              variant="outline" 
              className={cn(
                "text-[10px] gap-1",
                connected 
                  ? "text-emerald-400 border-emerald-500/30 bg-emerald-500/10" 
                  : "text-red-400 border-red-500/30 bg-red-500/10"
              )}
            >
              {connected ? (
                <>
                  <Wifi className="h-3 w-3" />
                  Connected
                </>
              ) : (
                <>
                  <WifiOff className="h-3 w-3" />
                  Connecting...
                </>
              )}
            </Badge>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearMessages}
            className="text-muted-foreground hover:text-destructive"
            data-testid="clear-chat-btn"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
        
        {/* Controls */}
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-2">
            <Switch
              id="thinking-toggle"
              checked={enableThinking}
              onCheckedChange={setEnableThinking}
              data-testid="thinking-toggle"
            />
            <Label htmlFor="thinking-toggle" className="text-xs text-muted-foreground flex items-center gap-1">
              <Brain className="h-3.5 w-3.5" />
              Thinking Mode
            </Label>
          </div>
          
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Tier:</Label>
            <Select value={forceTier} onValueChange={setForceTier}>
              <SelectTrigger className="h-7 w-32 text-xs" data-testid="tier-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto Route</SelectItem>
                <SelectItem value="tier1_local_fast">Local Fast</SelectItem>
                <SelectItem value="tier2_local_smart">Local Smart</SelectItem>
                <SelectItem value="tier3_cloud">Cloud</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <Sparkles className="h-12 w-12 mb-4 opacity-50" />
              <p className="text-sm">Start a conversation with Ellie</p>
              <p className="text-xs mt-1">Real-time WebSocket connection active</p>
            </div>
          )}
          
          {messages.map((message) => (
            <div
              key={message.id}
              className={cn(
                'flex gap-3 message-enter',
                message.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              {message.role === 'assistant' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                  <Bot className="h-4 w-4 text-primary" />
                </div>
              )}
              
              <div className={cn(
                'max-w-[80%] rounded-lg px-4 py-3',
                message.role === 'user'
                  ? 'bg-secondary text-foreground'
                  : 'bg-card border border-border',
                message.error && 'border-destructive/50'
              )}>
                {/* Assistant meta info */}
                {message.role === 'assistant' && message.tier && (
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border/50">
                    <TierBadge tier={message.tier} size="sm" />
                    {message.cache_hit && (
                      <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded">
                        CACHED
                      </span>
                    )}
                    <div className="flex items-center gap-2 ml-auto text-[10px] text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatLatency(message.latency_ms)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Coins className="h-3 w-3" />
                        ${message.cost_estimate?.toFixed(4)}
                      </span>
                    </div>
                  </div>
                )}

                {/* Thinking block */}
                {message.thinking && (
                  <div className="mb-3">
                    <button
                      onClick={() => toggleThinking(message.id)}
                      className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Brain className="h-3.5 w-3.5" />
                      <span>Thinking</span>
                      {expandedThinking[message.id] ? (
                        <ChevronUp className="h-3.5 w-3.5" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {expandedThinking[message.id] && (
                      <div className="mt-2 p-3 rounded bg-secondary/50 border border-border/50 scanlines">
                        <pre className="text-xs font-mono text-muted-foreground whitespace-pre-wrap">
                          {message.thinking}
                        </pre>
                      </div>
                    )}
                  </div>
                )}

                {/* Message content */}
                <div className="text-sm whitespace-pre-wrap">{message.content}</div>
                
                {/* Feedback button for assistant messages */}
                {message.role === 'assistant' && message.tier && !message.error && (
                  <div className="mt-2 pt-2 border-t border-border/30">
                    <button
                      onClick={() => {
                        // Find the user message that triggered this response
                        const msgIndex = messages.findIndex(m => m.id === message.id);
                        const userMsg = msgIndex > 0 ? messages[msgIndex - 1] : null;
                        openFeedbackDialog(message, userMsg?.content || '');
                      }}
                      className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-amber-400 transition-colors"
                      data-testid="feedback-btn"
                    >
                      <Flag className="h-3 w-3" />
                      <span>Wrong classification?</span>
                    </button>
                  </div>
                )}
              </div>

              {message.role === 'user' && (
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                  <User className="h-4 w-4 text-muted-foreground" />
                </div>
              )}
            </div>
          ))}

          {loading && (
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                <Bot className="h-4 w-4 text-primary" />
              </div>
              <div className="bg-card border border-border rounded-lg px-4 py-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Processing...</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input area */}
      <div className="chat-input-area p-4 flex-shrink-0">
        <div className="flex gap-2">
          <Textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={connected ? "Type your message... (Enter to send)" : "Connecting..."}
            className="min-h-[60px] max-h-[120px] resize-none bg-secondary border-border"
            disabled={loading || !connected}
            data-testid="chat-input"
          />
          <Button
            onClick={handleSend}
            disabled={!input.trim() || loading || !connected}
            className="px-4"
            data-testid="send-message-btn"
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </Card>

    {/* Feedback Dialog */}
    <Dialog open={feedbackDialog.open} onOpenChange={(open) => setFeedbackDialog({ open, message: null })}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Flag className="h-5 w-5 text-amber-400" />
            Report Misclassification
          </DialogTitle>
          <DialogDescription>
            Help improve routing by correcting this classification
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {feedbackDialog.originalQuery && (
            <div className="p-3 rounded bg-secondary/50 border border-border">
              <p className="text-xs text-muted-foreground mb-1">Original query:</p>
              <p className="text-sm">"{feedbackDialog.originalQuery}"</p>
            </div>
          )}
          
          {feedbackDialog.message && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Routed to:</span>
              <TierBadge tier={feedbackDialog.message.tier} size="sm" />
            </div>
          )}
          
          <div className="space-y-2">
            <Label>What should this be classified as?</Label>
            <Select value={selectedCorrection} onValueChange={setSelectedCorrection}>
              <SelectTrigger>
                <SelectValue placeholder="Select correct intent..." />
              </SelectTrigger>
              <SelectContent>
                {INTENT_OPTIONS.map(intent => (
                  <SelectItem key={intent.value} value={intent.value}>
                    <div className="flex items-center gap-2">
                      <span>{intent.label}</span>
                      <TierBadge tier={intent.tier} size="sm" showLabel={false} />
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setFeedbackDialog({ open: false, message: null })}>
            Cancel
          </Button>
          <Button onClick={handleSubmitFeedback} disabled={!selectedCorrection}>
            Submit Feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default ChatInterface;
